import { Lightbulb, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { pluralize } from '@/portainer/helpers/strings';
import { useCurrentUser } from '@/react/hooks/useUser';
import { isPureAdmin } from '@/portainer/users/user.helpers';
import { Widget } from '@/react/components/Widget';

import { PageHeader } from '@@/PageHeader';
import { Icon } from '@@/Icon';
import { Badge } from '@@/Badge';

import { RECOMMENDATION_CATEGORY } from './types';
import { useRecommendations } from './useRecommendations';
import { CategorySection } from './CategorySection';
import { CelebrationEmptyState } from './CelebrationEmptyState';
import {
  GovernanceProgressBar,
  GovernanceChecklist,
  GOVERNANCE_CHECK_COUNT,
  computeGovernanceCompletion,
} from './GovernanceProgressBar';
import {
  selectRecommendations,
  emptyCategorized,
  emptySeverityCounts,
} from './selectRecommendations';

const CATEGORY_ORDER = [
  RECOMMENDATION_CATEGORY.OPERATIONS,
  RECOMMENDATION_CATEGORY.SECURITY,
  RECOMMENDATION_CATEGORY.GOVERNANCE,
] as const;

export function RecommendationsView() {
  const { user } = useCurrentUser();
  const isAdmin = isPureAdmin(user);
  const [isGovernanceExpanded, setIsGovernanceExpanded] = useState(false);
  const { data, isLoading } = useRecommendations({
    enabled: isAdmin,
    select: selectRecommendations,
  });
  const {
    recommendations = [],
    categorized = emptyCategorized,
    severityCounts = emptySeverityCounts,
  } = data ?? {};

  if (!isAdmin) {
    return null;
  }

  const { passCount: governancePassCount, percent: completionPercent } =
    computeGovernanceCompletion(recommendations);

  return (
    <>
      <PageHeader
        title="Recommendations"
        breadcrumbs={[{ label: 'Recommendations' }]}
      />
      <div className="mx-4">
        <Widget>
          <Widget.Title
            title="Recommendations"
            icon={<Icon icon={Lightbulb} />}
          >
            <div className="flex items-center gap-2">
              {severityCounts.error > 0 && (
                <Badge type="danger" bordered>
                  {severityCounts.error} critical
                </Badge>
              )}
              {severityCounts.warning > 0 && (
                <Badge type="warn" bordered>
                  {severityCounts.warning}{' '}
                  {pluralize(severityCounts.warning, 'warning')}
                </Badge>
              )}
              {severityCounts.info > 0 && (
                <Badge type="info" bordered>
                  {severityCounts.info} info
                </Badge>
              )}
            </div>
          </Widget.Title>

          <Widget.Body loading={isLoading}>
            <div className="space-y-4 p-4">
              <p className="text-sm text-gray-7 th-highcontrast:text-white th-dark:text-gray-4">
                Recommendations help you get the most out of your Portainer
                setup by surfacing actionable suggestions when it detects
                environment issues or Portainer configuration gaps.
                <br /> Each recommendation includes a direct action to take you
                straight to the relevant area of Portainer to resolve it.
              </p>
              <div className="flex flex-col gap-2 rounded-lg !border ![border-style:solid] !border-gray-4 !bg-gray-2 p-4 th-highcontrast:!border-white th-highcontrast:!bg-gray-true-10/60 th-dark:!border-gray-7 th-dark:!bg-gray-true-9/40">
                <button
                  type="button"
                  className="flex w-full cursor-pointer appearance-none items-center justify-between border-0 bg-transparent p-0 text-left shadow-none outline-hidden"
                  onClick={() => setIsGovernanceExpanded((v) => !v)}
                  aria-expanded={isGovernanceExpanded}
                  data-cy="governance-section-toggle"
                >
                  <span className="flex items-center gap-1.5">
                    <Icon
                      icon={isGovernanceExpanded ? ChevronDown : ChevronRight}
                      className="h-4 w-4 text-gray-6 th-highcontrast:text-white th-dark:text-white"
                    />
                    <span className="text-sm font-medium text-gray-8 th-highcontrast:text-white th-dark:text-white">
                      Fleet Governance: {completionPercent}% complete
                    </span>
                  </span>
                  <span className="text-xs text-gray-6 th-highcontrast:text-white th-dark:text-white">
                    {governancePassCount} of {GOVERNANCE_CHECK_COUNT} checks
                    passed
                  </span>
                </button>
                {isGovernanceExpanded && (
                  <>
                    <GovernanceProgressBar recommendations={recommendations} />
                    <GovernanceChecklist recommendations={recommendations} />
                  </>
                )}
              </div>

              {recommendations.length === 0 ? (
                <CelebrationEmptyState />
              ) : (
                <div className="space-y-4">
                  {CATEGORY_ORDER.map((category) => (
                    <CategorySection
                      key={category}
                      category={category}
                      items={categorized[category]}
                    />
                  ))}
                </div>
              )}
            </div>
          </Widget.Body>
        </Widget>
      </div>
    </>
  );
}
